import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../../components/common/Modal';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { useToast } from '../../../components/common/Toast';
import { formatCurrency, formatDecimal } from '../../../utils/format';
import { useLanguage } from '../../../features/language/useLanguage';
import { salesOrderService } from '../../../services/salesOrderService';
import type { SalesOrder } from '../../../types/salesOrder';
import type {
  CustomerFinishedGood,
  CustomerPurchaseDetailResponse,
  CustomerReportResponse,
} from '../../../services/report.service';
import { CreateSalesOrderForm } from '../../sales-orders/CreateSalesOrderForm';
import { CustomerPurchaseChart } from './CustomerPurchaseChart';
import { CustomerFgHistory } from './CustomerFgHistory';

interface CustomerReportSectionProps {
  list: CustomerReportResponse;
  loading: boolean;
  error: string | null;
  currencySymbol: string;
  selectedCustomerId: string | null;
  onSelectCustomer: (customerId: string) => void;
  detail: CustomerPurchaseDetailResponse | null;
  detailLoading: boolean;
  detailError: string | null;
}

export const CustomerReportSection: React.FC<CustomerReportSectionProps> = ({
  list,
  loading,
  error,
  currencySymbol,
  selectedCustomerId,
  onSelectCustomer,
  detail,
  detailLoading,
  detailError,
}) => {
  const { t } = useLanguage();
  const { showToast } = useToast();
  const [search, setSearch] = useState('');
  const [viewingOrder, setViewingOrder] = useState<SalesOrder | null>(null);
  const [selectedFg, setSelectedFg] = useState<CustomerFinishedGood | null>(null);

  // Reset the drill-down when switching customers so the chart shows first.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedFg(null);
  }, [selectedCustomerId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list.customers;
    return list.customers.filter(
      c => c.customerName.toLowerCase().includes(q) || c.customerCode.toLowerCase().includes(q)
    );
  }, [list.customers, search]);

  const openOrder = async (orderId: string) => {
    const order = await salesOrderService.getById(orderId);
    if (!order) {
      showToast(t('reports.customer.orderLoadFailed'), 'error');
      return;
    }
    setViewingOrder(order);
  };

  const handleFgClick = (itemId: string) => {
    const fg = detail?.finishedGoods.find(f => f.itemId === itemId) ?? null;
    setSelectedFg(fg);
  };

  if (loading) {
    return (
      <section className="bg-white border border-gray-200 rounded-lg p-6" data-test-id="customer-report-section">
        <LoadingSpinner text={t('reports.customer.loading')} />
      </section>
    );
  }
  if (error) {
    return (
      <section className="bg-white border border-gray-200 rounded-lg p-6" data-test-id="customer-report-section">
        <p className="text-sm text-red-600">{error}</p>
      </section>
    );
  }

  return (
    <section className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-test-id="customer-report-section">
      {/* Customer list */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden lg:col-span-1">
        <div className="px-4 py-3 border-b border-gray-200 space-y-2">
          <p className="text-sm font-semibold text-gray-900">{t('reports.customer.listTitle')}</p>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('reports.customer.searchPlaceholder')}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            data-test-id="customer-report-search"
          />
        </div>
        <div className="max-h-[520px] overflow-y-auto divide-y divide-gray-100">
          {filtered.length === 0 ? (
            <p className="px-4 py-4 text-sm text-gray-500">{t('reports.customer.emptyList')}</p>
          ) : (
            filtered.map(c => {
              const isActive = c.customerId === selectedCustomerId;
              return (
                <button
                  key={c.customerId}
                  type="button"
                  onClick={() => onSelectCustomer(c.customerId)}
                  data-test-id={`customer-report-row-${c.customerId}`}
                  className={`w-full text-left px-4 py-3 transition-colors ${
                    isActive ? 'bg-primary-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-gray-900">{c.customerName}</span>
                    <span className="text-xs text-gray-500">{c.customerCode}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
                    <span>{formatDecimal(c.orderCount, 0)} {t('reports.customer.table.orders')}</span>
                    <span>{formatCurrency(c.totalRevenue, currencySymbol)}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-gray-400">
                    {formatDecimal(c.totalPcs, 0)} {t('reports.customer.table.pcs')} · {c.lastOrderDate || t('reports.customer.summary.none')}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Detail */}
      <div className="lg:col-span-2 space-y-4">
        {!selectedCustomerId ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-sm text-gray-500">
            {t('reports.customer.selectPrompt')}
          </div>
        ) : detailLoading ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <LoadingSpinner text={t('reports.customer.detailLoading')} />
          </div>
        ) : detailError ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-sm text-red-600">{detailError}</div>
        ) : detail ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500">{t('reports.customer.summary.topFinishedGood')}</p>
                <p className="mt-1 text-sm font-semibold text-gray-900">
                  {detail.summary.topFinishedGood
                    ? `${detail.summary.topFinishedGood.name} (${formatDecimal(detail.summary.topFinishedGood.pcs, 0)})`
                    : t('reports.customer.summary.none')}
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500">{t('reports.customer.summary.totalPcs')}</p>
                <p className="mt-1 text-xl font-semibold text-gray-900">{formatDecimal(detail.summary.totalPcs, 0)}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500">{t('reports.customer.summary.totalOrders')}</p>
                <p className="mt-1 text-xl font-semibold text-gray-900">{formatDecimal(detail.summary.totalOrders, 0)}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500">{t('reports.customer.summary.totalRevenue')}</p>
                <p className="mt-1 text-xl font-semibold text-gray-900">
                  {formatCurrency(detail.summary.totalRevenue, currencySymbol)}
                </p>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-gray-900 mb-3">{t('reports.customer.chartTitle')}</p>
              {selectedFg ? (
                <CustomerFgHistory
                  fg={selectedFg}
                  onBack={() => setSelectedFg(null)}
                  onOpenOrder={orderId => void openOrder(orderId)}
                />
              ) : (
                <CustomerPurchaseChart
                  finishedGoods={detail.finishedGoods}
                  onFinishedGoodClick={handleFgClick}
                />
              )}
            </div>
          </>
        ) : null}
      </div>

      {/* Read-only sales order */}
      <Modal
        isOpen={!!viewingOrder}
        onClose={() => setViewingOrder(null)}
        title={viewingOrder?.orderNumber ?? ''}
        maxWidth="7xl"
      >
        {viewingOrder && (
          <CreateSalesOrderForm
            key={`customer-so-view-${viewingOrder.id}`}
            isOpen={!!viewingOrder}
            initialData={viewingOrder}
            onSubmit={async () => {}}
            onCancel={() => setViewingOrder(null)}
            readOnly
          />
        )}
      </Modal>
    </section>
  );
};
